// The drawer's stages, kept apart from the form so the rules can be read and
// tested without rendering anything. The form holds the fields; this holds
// where the recruiter is and how they got there.

export type Stage = 'employer' | 'contact' | 'job'

export type EmployerMode = 'search' | 'picked' | 'typed'

export interface AddFlowState {
  stage: Stage
  employerMode: EmployerMode
  /** Kept across Change and Search instead, so the results come straight back. */
  query: string
  pickedDot: string | null
  /** The known-contacts list is offered; a pick or "a different contact" dismisses it. */
  contactList: boolean
  /**
   * Job information has been shown at least once. Until then a Save would
   * store dates and answers nobody has looked at.
   */
  reachedJob: boolean
}

export type AddFlowEvent =
  | { type: 'query'; query: string }
  | { type: 'pickCarrier'; dotNumber: string | null; offerList: boolean }
  | { type: 'addAsTyped' }
  | { type: 'changeEmployer' }
  | { type: 'searchInstead' }
  | { type: 'continueEmployer'; offerList: boolean }
  | { type: 'pickContact' }
  | { type: 'differentContact' }
  | { type: 'showContactList' }
  | { type: 'continueContact' }
  | { type: 'changeContact' }
  | { type: 'showStage'; stage: Stage }

export const initialFlow = (): AddFlowState => ({
  stage: 'employer',
  employerMode: 'search',
  query: '',
  pickedDot: null,
  contactList: false,
  reachedJob: false,
})

export const addEmploymentFlow = (
  state: AddFlowState,
  event: AddFlowEvent
): AddFlowState => {
  switch (event.type) {
    case 'query':
      return { ...state, query: event.query }
    case 'pickCarrier':
      return {
        ...state,
        stage: 'contact',
        employerMode: 'picked',
        pickedDot: event.dotNumber,
        contactList: event.offerList,
      }
    case 'addAsTyped':
      return {
        ...state,
        stage: 'employer',
        employerMode: 'typed',
        pickedDot: null,
      }
    case 'changeEmployer':
      return {
        ...state,
        stage: 'employer',
        employerMode: 'search',
        pickedDot: null,
        contactList: false,
      }
    case 'searchInstead':
      return { ...state, stage: 'employer', employerMode: 'search' }
    case 'continueEmployer':
      return { ...state, stage: 'contact', contactList: event.offerList }
    case 'pickContact':
    case 'differentContact':
      return { ...state, contactList: false }
    case 'showContactList':
      return { ...state, contactList: true }
    case 'continueContact':
      return { ...state, stage: 'job', reachedJob: true }
    case 'changeContact':
      return { ...state, stage: 'contact' }
    case 'showStage':
      return {
        ...state,
        stage: event.stage,
        reachedJob: state.reachedJob || event.stage === 'job',
      }
    default:
      return state
  }
}

export const BLANK_ADDRESS = {
  street: '',
  city: '',
  state: '',
  zipCode: '',
  country: '',
}

/** The registry's facts under another employer would be a record that claims something untrue. */
export const fieldsAfterLeavingPick = () => ({
  dotNumber: '',
  address: { ...BLANK_ADDRESS },
})

const nameKey = (name: string | null | undefined) =>
  (name ?? '').trim().toLowerCase()
const dotKey = (dotNumber: string | null | undefined) =>
  (dotNumber ?? '').trim()

export interface EmployerIdentity {
  name: string | null | undefined
  dotNumber?: string | null
}

/**
 * A different employer means a different contact. FMCSA lists one carrier
 * under many registrations with the same legal name, so two DOT numbers are
 * two employers even when the names match.
 */
export const replacesEmployer = (
  previous: EmployerIdentity | null | undefined,
  next: EmployerIdentity
): boolean => {
  if (!previous || !nameKey(previous.name)) return false
  if (nameKey(previous.name) !== nameKey(next.name)) return true
  const previousDot = dotKey(previous.dotNumber)
  const nextDot = dotKey(next.dotNumber)
  return Boolean(previousDot && nextDot && previousDot !== nextDot)
}

const EMPLOYER_FIELDS = ['companyName', 'dotNumber', 'address']
const CONTACT_FIELDS = ['contactName', 'email', 'phone']

/** The stage that shows the field, so a failed Save lands where the error is. */
export const stageForField = (field: string): Stage => {
  const root = field.split('.')[0]
  if (EMPLOYER_FIELDS.includes(root)) return 'employer'
  if (CONTACT_FIELDS.includes(root)) return 'contact'
  return 'job'
}
